import { DRAWING_HANDLE_EDGE_INSET_PX } from "./drawingConstants";
import { rayEnd } from "./drawingRay";
import { backgroundFills, mainStroke } from "./drawingShapeParts";
import {
  BACKGROUND_STYLE,
  FIBONACCI_FAN_STYLE,
  FIBONACCI_RETRACEMENT_STYLE,
  PITCHFORK_STYLE,
} from "./drawingStyles";
import { fibonacciRetracementShape } from "./fibonacciRetracement";
import { fibonacciFanShape } from "./fibonacciFan";
import { parallelChannelDragTarget, parallelChannelHandles, parallelChannelShape } from "./parallelChannel";
import { pitchforkShape } from "./pitchfork";

/** Drawing tools (research §1.5). */
export const DRAWING_TOOL = {
  HORIZONTAL_LINE: "horizontal-line",
  VERTICAL_LINE: "vertical-line",
  RAY: "ray",
  FIBONACCI_RETRACEMENT: "fibonacci-retracement",
  FIBONACCI_FAN: "fibonacci-fan",
  TREND_LINE: "trend-line",
  PARALLEL_CHANNEL: "parallel-channel",
  RECTANGLE: "rectangle",
  ANDREWS_PITCHFORK: "andrews-pitchfork",
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const insideEdges = (value, size) =>
  clamp(value, DRAWING_HANDLE_EDGE_INSET_PX, Math.max(DRAWING_HANDLE_EDGE_INSET_PX, size - DRAWING_HANDLE_EDGE_INSET_PX));

/**
 * What a tool draws. `defaultPoints`: where a new drawing lies, shares of the
 * chart (drawingPlacement.js); `shape` gets the points in CSS px of the pane, the
 * pane and the style, and returns strokes, fills and labels (drawingShapeParts.js);
 * `handles` places the handles, one per point in the order of the points (on the
 * points by default); `dragTarget`: where the point of a dragged handle goes
 * (under the pointer by default); `hasPriceLabel`: the price of the first point is
 * shown on the price axis; `style`: what its drawings have beyond colour and width (drawingStyles.js).
 */
const TOOLS = [
  {
    id: DRAWING_TOOL.HORIZONTAL_LINE,
    pointCount: 1,
    defaultPoints: [[0.5, 0.5]],
    hasPriceLabel: true,
    shape: ([{ y }], { width }, style) => ({ strokes: [mainStroke(style, [[0, y, width, y]])], fills: [] }),
    handles: ([{ x, y }], { width }) => [{ x: insideEdges(x, width), y }],
  },
  {
    id: DRAWING_TOOL.VERTICAL_LINE,
    pointCount: 1,
    defaultPoints: [[0.5, 0.5]],
    shape: ([{ x }], { height }, style) => ({ strokes: [mainStroke(style, [[x, 0, x, height]])], fills: [] }),
    handles: ([{ x, y }], { height }) => [{ x, y: insideEdges(y, height) }],
  },
  {
    id: DRAWING_TOOL.RAY,
    pointCount: 2,
    defaultPoints: [
      [0.2, 0.62],
      [0.8, 0.38],
    ],
    shape: ([from, to], pane, style) => {
      const end = rayEnd(from, to, pane);

      return { strokes: [mainStroke(style, [[from.x, from.y, end.x, end.y]])], fills: [] };
    },
  },
  {
    id: DRAWING_TOOL.TREND_LINE,
    pointCount: 2,
    defaultPoints: [
      [0.2, 0.62],
      [0.8, 0.38],
    ],
    shape: ([from, to], pane, style) => ({ strokes: [mainStroke(style, [[from.x, from.y, to.x, to.y]])], fills: [] }),
  },
  {
    id: DRAWING_TOOL.RECTANGLE,
    pointCount: 2,
    defaultPoints: [
      [0.3, 0.7],
      [0.7, 0.38],
    ],
    style: BACKGROUND_STYLE,
    shape: ([{ x: x0, y: y0 }, { x: x1, y: y1 }], pane, style) => ({
      strokes: [
        mainStroke(style, [
          [x0, y0, x1, y0],
          [x1, y0, x1, y1],
          [x1, y1, x0, y1],
          [x0, y1, x0, y0],
        ]),
      ],
      fills: backgroundFills(style, [[x0, y0, x1, y0, y1, y1]]),
    }),
  },
  {
    id: DRAWING_TOOL.FIBONACCI_RETRACEMENT,
    pointCount: 2,
    defaultPoints: [
      [0.2, 0.5],
      [0.8, 0.75],
    ],
    style: FIBONACCI_RETRACEMENT_STYLE,
    shape: fibonacciRetracementShape,
  },
  {
    id: DRAWING_TOOL.FIBONACCI_FAN,
    pointCount: 2,
    defaultPoints: [
      [0.2, 0.85],
      [0.8, 0.37],
    ],
    style: FIBONACCI_FAN_STYLE,
    shape: fibonacciFanShape,
  },
  {
    id: DRAWING_TOOL.PARALLEL_CHANNEL,
    pointCount: 3,
    defaultPoints: [
      [0.2, 0.53],
      [0.8, 0.37],
      [0.5, 0.77],
    ],
    style: BACKGROUND_STYLE,
    shape: parallelChannelShape,
    handles: parallelChannelHandles,
    dragTarget: parallelChannelDragTarget,
  },
  {
    id: DRAWING_TOOL.ANDREWS_PITCHFORK,
    pointCount: 3,
    defaultPoints: [
      [0.2, 0.61],
      [0.22, 0.48],
      [0.27, 0.56],
    ],
    style: PITCHFORK_STYLE,
    shape: pitchforkShape,
  },
];

/**
 * @param {string} id - DRAWING_TOOL value
 * @returns {object|null} the tool; null for an unknown id
 */
export const getDrawingTool = (id) => TOOLS.find((tool) => tool.id === id) || null;
